import { useState } from 'react'

export type DetailFormValues = {
  type: string
  destination: string
  startDate: string
  endDate: string
}

// This pilot's own detail types, matching the facts already established
// elsewhere in the supervisor workspace (SupervisorDashboardPage.tsx's own
// DETAILED_OUT list, boardCodes.ts's Temporary duty and Detailed out
// codes), since no type catalog exists yet.
const DETAIL_TYPES = ['Task Force', 'Training Detail', 'Temporary duty', 'Detailed out']

/**
 * docs/product/scheduling-model.md's "Details": "a type, the originating
 * team, the destination, start and end dates." The originating team is
 * left out of this form: it is whichever team the employee, or employees
 * for a bulk detail, already belong to, which the caller already knows
 * and this pilot's roster never varies. There is no field yet for the
 * schedule an employee keeps while detailed (shift, work status,
 * activity), since that edits the schedule the planning board owns.
 * Shared by the single-employee form on the profile and the bulk form on
 * the directory, so the two stay identical rather than drifting apart.
 */
export default function DetailForm({
  onSave,
  onCancel,
}: {
  onSave: (values: DetailFormValues) => void
  onCancel: () => void
}) {
  const [type, setType] = useState(DETAIL_TYPES[0])
  const [destination, setDestination] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const canSave = destination.trim() !== '' && startDate !== '' && endDate !== '' && endDate >= startDate

  return (
    <div className="flex flex-col gap-3 rounded-surface border border-line bg-canvas p-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="detail-type" className="mb-1 block text-caption font-bold text-ink-muted">
            Type
          </label>
          <select
            id="detail-type"
            value={type}
            onChange={(event) => setType(event.target.value)}
            className="min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small"
          >
            {DETAIL_TYPES.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="detail-destination" className="mb-1 block text-caption font-bold text-ink-muted">
            Destination
          </label>
          <input
            id="detail-destination"
            type="text"
            value={destination}
            onChange={(event) => setDestination(event.target.value)}
            placeholder="e.g. Miami Field Office"
            className="min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small"
          />
        </div>
        <div>
          <label htmlFor="detail-start" className="mb-1 block text-caption font-bold text-ink-muted">
            Start date
          </label>
          <input
            id="detail-start"
            type="date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
            className="min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small"
          />
        </div>
        <div>
          <label htmlFor="detail-end" className="mb-1 block text-caption font-bold text-ink-muted">
            End date
          </label>
          <input
            id="detail-end"
            type="date"
            min={startDate || undefined}
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
            className="min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small"
          />
        </div>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => canSave && onSave({ type, destination: destination.trim(), startDate, endDate })}
          disabled={!canSave}
          className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover disabled:opacity-40"
        >
          Save detail
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex min-h-11 items-center justify-center rounded-control px-4 text-small font-bold text-ink-muted hover:bg-surface-2"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
